// Global search suggestions (WEBSITE feature requirements 3.2). Pure model:
// it turns a typed query plus the two live answers into the four required
// groups, in this order.
//
//   1. Direct research actions  parsed from the query itself (contract, ticker,
//      X handle, X post, Axiom or DEX Screener URL, destination name)
//   2. Loaded records           rows this browser already holds
//   3. Retained archive         GET /api/beta/evidence-archive/search
//   4. Entities                 GET /api/beta/search/entities
//
// Rules it enforces: never guess between duplicate tickers (an ambiguous
// archive answer renders its candidates and refuses the single blind open),
// every match states why it is available, and a group that returns nothing
// states what was searched instead of disappearing.

import type {
  ArchiveCall,
  ArchiveCallsResponse,
  SearchEntitiesResponse,
  SearchEntity,
  WorkspaceRole,
} from '../api/types';
import { shortContract } from '../identity/contract';
import { parseResearchQuery, type ResearchQuery } from '../identity/parseQuery';
import { displayLabel } from '../identity/text';
import type { ViewId } from '../router/useView';
import { formatZurichDayTime } from '../time/zurich';
import { DESTINATIONS, destinationAllowed } from './destinations';

export type SearchSectionId = 'actions' | 'loaded' | 'archive' | 'entities';

export type SearchAction =
  | { readonly kind: 'view'; readonly view: ViewId; readonly q?: string }
  | { readonly kind: 'call'; readonly callId: string };

export interface SearchSuggestion {
  readonly id: string;
  readonly label: string;
  // Rendered after the label in muted 12 px.
  readonly detail: string | null;
  // Contracts and record ids render in IBM Plex Mono; names do not.
  readonly mono: boolean;
  // Why this match is available, or why it cannot be opened.
  readonly availability: string;
  readonly disabled: boolean;
  readonly action: SearchAction | null;
}

export type SectionState = 'ready' | 'loading' | 'empty' | 'error' | 'ambiguous' | 'unsent';

export interface SearchSection {
  readonly id: SearchSectionId;
  readonly title: string;
  readonly state: SectionState;
  // One sentence naming what was searched, or why the group is unavailable.
  readonly reason: string;
  readonly items: readonly SearchSuggestion[];
}

export interface LoadedRecordSource {
  // Printed in the group reason so the reader knows what "loaded" means here.
  readonly label: string;
  readonly rows: readonly ArchiveCall[];
}

export interface SearchInputs {
  readonly query: string;
  readonly loaded: LoadedRecordSource;
  // isPlaceholder is TanStack's isPlaceholderData: the payload belongs to the
  // PREVIOUS query and is kept only so the popover does not blink. It is never
  // an answer about the text in the field now, so the groups below refuse to
  // count it or list it (redo-1 fix round 2, P1: "doland" printed 109 bonk
  // records as matches for three seconds).
  readonly archive: {
    readonly data: ArchiveCallsResponse | undefined;
    readonly isPending: boolean;
    readonly isPlaceholder: boolean;
    readonly error: unknown;
  };
  readonly entities: {
    readonly data: SearchEntitiesResponse | undefined;
    readonly isPending: boolean;
    readonly isPlaceholder: boolean;
    readonly error: unknown;
  };
  readonly maxPerGroup: number;
  // Set when the two server searches were never sent (the query is longer
  // than the endpoint accepts). The groups say so instead of vanishing, and
  // the parsed research actions still work, which is what saves a pasted
  // 200 character DEX Screener link.
  readonly serverUnsentReason?: string | null;
  readonly role?: WorkspaceRole | null;
}

const ENTITY_KIND_LABELS: Readonly<Record<string, string>> = {
  wallet: 'Wallet',
  provider: 'Provider',
  group: 'Source group',
  person: 'Caller',
  creator: 'Creator',
};

// Destination ids an entity kind may open. The backend names a destination on
// some rows; anything else falls back to the kind map, and an unmapped kind is
// rendered with its reason instead of a wrong link.
const ENTITY_KIND_VIEWS: Readonly<Record<string, ViewId>> = {
  wallet: 'wallets',
  provider: 'health',
  group: 'sources',
  person: 'callers',
  creator: 'creators',
};

function reasonOfError(error: unknown): string {
  return error instanceof Error && error.message
    ? error.message
    : 'The request failed before the server answered.';
}

// Same anatomy as a call row (CONTRACT "Call row"): the name leads in 600 and
// the ticker follows muted; with no observed metadata the mono short address
// is the whole identity.
function identityOf(row: ArchiveCall): {
  readonly label: string;
  readonly ticker: string | null;
  readonly mono: boolean;
} {
  const name = displayLabel(row.coinName, '');
  const ticker = displayLabel(row.ticker, '');
  if (name) return { label: name, ticker: ticker || null, mono: false };
  if (ticker) return { label: ticker, ticker: null, mono: false };
  return { label: shortContract(row.contractAddress), ticker: null, mono: true };
}

function archiveSuggestion(row: ArchiveCall, prefix: string): SearchSuggestion {
  const identity = identityOf(row);
  const caller = displayLabel(row.callerLabel, '');
  const group = displayLabel(row.groupLabel, row.sourceLabel);
  const at = formatZurichDayTime(row.callEventAt);
  const where = caller ? `${caller} in ${group}` : group;
  return {
    id: `${prefix}:${row.id}`,
    label: identity.label,
    detail: identity.ticker ? `${identity.ticker}, ${where}` : where,
    mono: identity.mono,
    availability: at ? `Observed ${at}` : 'Observation time unavailable',
    disabled: false,
    action: { kind: 'call', callId: row.id },
  };
}

// Destination names are an accepted input (contract 3.1). Matched on a
// case-insensitive prefix or word start so "cal" reaches Live Calls and
// Caller Intelligence without matching every label that contains an "a".
export function matchDestinations(
  query: string,
  role: WorkspaceRole | null = null,
): ReadonlyArray<{ readonly id: ViewId; readonly label: string }> {
  const needle = query.trim().toLowerCase();
  if (needle.length < 2) return [];
  return DESTINATIONS.filter((destination) => {
    if (!destinationAllowed(destination, role)) return false;
    const label = destination.label.toLowerCase();
    return label.startsWith(needle) || label.split(' ').some((word) => word.startsWith(needle));
  });
}

function actionsFor(
  query: string,
  parsed: ResearchQuery,
  limit: number,
  role: WorkspaceRole | null,
): SearchSection {
  const items: SearchSuggestion[] = [];
  const trimmed = query.trim();

  if (parsed.kind === 'coin') {
    const isContract =
      parsed.queryKind === 'contract' ||
      parsed.queryKind === 'axiom_url' ||
      parsed.queryKind === 'dexscreener_url';
    const label = isContract
      ? shortContract(parsed.value)
      : parsed.queryKind === 'ticker'
        ? `$${parsed.value}`
        : parsed.value;
    const source =
      parsed.queryKind === 'axiom_url'
        ? ' from the Axiom link'
        : parsed.queryKind === 'dexscreener_url'
          ? ' from the DEX Screener link'
          : '';
    items.push({
      id: 'action:checker',
      label: `Research ${label} in Coin Checker`,
      detail: null,
      mono: false,
      availability: isContract
        ? `Exact contract read from what you typed${source}.`
        : 'Coin Checker resolves the identity before it reads market data.',
      disabled: false,
      action: {
        kind: 'view',
        view: 'checker',
        q: parsed.queryKind === 'ticker' ? `$${parsed.value}` : parsed.value,
      },
    });
  }
  if (parsed.kind === 'x_account') {
    items.push({
      id: 'action:x-account',
      label: `Open @${parsed.value} in X`,
      detail: null,
      mono: false,
      availability: 'Read as an X handle.',
      disabled: false,
      action: { kind: 'view', view: 'x', q: `@${parsed.value}` },
    });
  }
  if (parsed.kind === 'x_post') {
    items.push({
      id: 'action:x-post',
      label: `Open the post by @${parsed.handle} in X`,
      detail: `post ${parsed.value}`,
      mono: true,
      availability: 'Read as an X post link.',
      disabled: false,
      action: { kind: 'view', view: 'x', q: trimmed },
    });
  }
  for (const destination of matchDestinations(trimmed, role)) {
    items.push({
      id: `action:view:${destination.id}`,
      label: `Go to ${destination.label}`,
      detail: null,
      mono: false,
      availability: 'Destination name.',
      disabled: false,
      action: { kind: 'view', view: destination.id },
    });
  }

  const capped = items.slice(0, limit);
  if (capped.length > 0) {
    return {
      id: 'actions',
      title: 'Research actions',
      state: 'ready',
      reason: 'Read from what you typed, before any request.',
      items: capped,
    };
  }
  return {
    id: 'actions',
    title: 'Research actions',
    state: 'empty',
    reason:
      parsed.kind === 'invalid'
        ? `Not a contract, ticker, handle or link (${parsed.reason.replaceAll('_', ' ')}).`
        : 'Nothing in this text reads as a contract, ticker, handle, link or destination name.',
    items: [],
  };
}

function loadedFor(query: string, loaded: LoadedRecordSource, limit: number): SearchSection {
  const needle = query.trim().toLowerCase();
  const rows = loaded.rows.filter((row) => {
    const haystack = [
      row.ticker,
      row.coinName,
      row.contractAddress,
      row.callerLabel,
      row.groupLabel,
      row.sourceLabel,
    ]
      .filter((value): value is string => typeof value === 'string' && value !== '')
      .join(' ')
      .toLowerCase();
    return haystack.includes(needle);
  });
  const items = rows.slice(0, limit).map((row) => archiveSuggestion(row, 'loaded'));
  if (items.length > 0) {
    return {
      id: 'loaded',
      title: 'Loaded records',
      state: 'ready',
      reason: `Matched inside ${loaded.label}.`,
      items,
    };
  }
  return {
    id: 'loaded',
    title: 'Loaded records',
    state: 'empty',
    reason: `No match inside ${loaded.label}.`,
    items: [],
  };
}

function ambiguitySection(response: ArchiveCallsResponse, limit: number): SearchSection {
  const items = response.ambiguity.candidates.slice(0, limit).map((candidate) => ({
    id: `ambiguous:${candidate.chain}:${candidate.contractAddress}`,
    label: shortContract(candidate.contractAddress),
    detail: `${candidate.chain}, ${candidate.records.toLocaleString('en-GB')} records`,
    mono: true,
    availability: 'Pick this exact contract to continue.',
    disabled: false,
    action: { kind: 'view', view: 'checker', q: candidate.contractAddress } as SearchAction,
  }));
  return {
    id: 'archive',
    title: 'Retained archive',
    state: 'ambiguous',
    reason: `More than one contract answers this text (${response.ambiguity.candidates.length} candidates${response.ambiguity.truncated ? ', list truncated' : ''}). Pick the exact contract.`,
    items,
  };
}

function archiveFor(
  input: SearchInputs['archive'],
  limit: number,
  unsent: string | null,
): SearchSection {
  if (unsent !== null) {
    return { id: 'archive', title: 'Retained archive', state: 'unsent', reason: unsent, items: [] };
  }
  // A placeholder payload answers the previous query, so it is not data here.
  const data = input.isPlaceholder ? undefined : input.data;
  if ((input.isPending || input.isPlaceholder) && !data) {
    return {
      id: 'archive',
      title: 'Retained archive',
      state: 'loading',
      reason: 'Searching the retained evidence archive.',
      items: [],
    };
  }
  if (input.error && !data) {
    return {
      id: 'archive',
      title: 'Retained archive',
      state: 'error',
      reason: `Archive search unavailable: ${reasonOfError(input.error)}`,
      items: [],
    };
  }
  if (!data) {
    return {
      id: 'archive',
      title: 'Retained archive',
      state: 'empty',
      reason: 'No answer from the archive yet.',
      items: [],
    };
  }
  if (data.ambiguity.state === 'ambiguous' && data.ambiguity.candidates.length > 0) {
    return ambiguitySection(data, limit);
  }
  const items = data.items.slice(0, limit).map((row) => archiveSuggestion(row, 'archive'));
  if (items.length > 0) {
    const total = data.coverage.matchingRecords.toLocaleString('en-GB');
    return {
      id: 'archive',
      title: 'Retained archive',
      state: 'ready',
      reason: `${total} retained records match; newest first.`,
      items,
    };
  }
  return {
    id: 'archive',
    title: 'Retained archive',
    state: 'empty',
    reason: 'No retained record matches this text.',
    items: [],
  };
}

function entitySuggestion(entity: SearchEntity): SearchSuggestion {
  const kindLabel = ENTITY_KIND_LABELS[entity.kind] ?? entity.kind;
  const view =
    (entity.destination && (ENTITY_KIND_VIEWS[entity.destination] ?? undefined)) ??
    ENTITY_KIND_VIEWS[entity.kind];
  const research = entity.researchValue ?? entity.identifier ?? entity.label;
  return {
    id: `entity:${entity.kind}:${entity.id}`,
    label: displayLabel(entity.label, entity.identifier ?? entity.id),
    detail: entity.detail ? displayLabel(entity.detail, kindLabel) : kindLabel,
    mono: entity.kind === 'wallet',
    availability: view
      ? `${kindLabel} observed in this workspace.`
      : `${kindLabel} match, but no destination is published for this kind yet.`,
    disabled: view === undefined,
    action: view ? { kind: 'view', view, q: research } : null,
  };
}

function entitiesFor(
  input: SearchInputs['entities'],
  limit: number,
  unsent: string | null,
): SearchSection {
  if (unsent !== null) {
    return { id: 'entities', title: 'Entities', state: 'unsent', reason: unsent, items: [] };
  }
  // A placeholder payload answers the previous query, so it is not data here.
  const data = input.isPlaceholder ? undefined : input.data;
  if ((input.isPending || input.isPlaceholder) && !data) {
    return {
      id: 'entities',
      title: 'Entities',
      state: 'loading',
      reason: 'Searching wallets, providers, groups, callers and creators.',
      items: [],
    };
  }
  if (input.error && !data) {
    return {
      id: 'entities',
      title: 'Entities',
      state: 'error',
      reason: `Entity search unavailable: ${reasonOfError(input.error)}`,
      items: [],
    };
  }
  if (!data) {
    return {
      id: 'entities',
      title: 'Entities',
      state: 'empty',
      reason: 'No answer from the entity search yet.',
      items: [],
    };
  }
  const items = data.items.slice(0, limit).map(entitySuggestion);
  const kinds = data.coverage?.kinds?.length
    ? data.coverage.kinds.join(', ')
    : 'wallets, providers, groups, callers, creators';
  if (items.length > 0) {
    return {
      id: 'entities',
      title: 'Entities',
      state: 'ready',
      reason: `Searched ${kinds}.`,
      items,
    };
  }
  return {
    id: 'entities',
    title: 'Entities',
    state: 'empty',
    reason: `No entity matches; searched ${kinds}.`,
    items: [],
  };
}

export function buildSearchSections(input: SearchInputs): ReadonlyArray<SearchSection> {
  const parsed = parseResearchQuery(input.query);
  const unsent = input.serverUnsentReason ?? null;
  return [
    actionsFor(input.query, parsed, input.maxPerGroup, input.role ?? null),
    loadedFor(input.query, input.loaded, input.maxPerGroup),
    archiveFor(input.archive, input.maxPerGroup, unsent),
    entitiesFor(input.entities, input.maxPerGroup, unsent),
  ];
}

// The keyboard order: every enabled item, in section order.
export function flattenSuggestions(
  sections: ReadonlyArray<SearchSection>,
): ReadonlyArray<SearchSuggestion> {
  return sections.flatMap((section) =>
    section.items.filter((item) => !item.disabled && item.action !== null),
  );
}

// Arrow keys wrap at both ends; an id that vanished between renders restarts
// from the top so focus is never lost on a stale highlight.
export function moveHighlight(
  currentId: string | null,
  step: 1 | -1,
  items: ReadonlyArray<SearchSuggestion>,
): string | null {
  if (items.length === 0) return null;
  const index = currentId === null ? -1 : items.findIndex((item) => item.id === currentId);
  if (index === -1) return (step === 1 ? items[0]?.id : items[items.length - 1]?.id) ?? null;
  return items[(index + step + items.length) % items.length]?.id ?? null;
}
